// frontend/src/pages/SubjectManagementPage.js
import { useState, useEffect } from 'react';
import { Button, Modal, Form, Input, Table, Space, Typography, Popconfirm } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { getAllSubjects, createSubject, updateSubject, deleteSubject } from '../services/subjectService';
import useResponsive from '../hooks/useResponsive'; // Import the hook
import { useNotification } from '../context/NotificationContext';

const { Title } = Typography;

const SubjectManagementPage = () => {
    const { message } = useNotification();
    const { isMobile } = useResponsive(); // Use the hook
    const [subjects, setSubjects] = useState([]);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [editingSubject, setEditingSubject] = useState(null);
    const [loading, setLoading] = useState(true);
    const [pageSize, setPageSize] = useState(10);
    const [form] = Form.useForm();

    useEffect(() => {
        // Subjects are not year-specific, so we don't need to listen to currentYear
        fetchData();
    }, []);

    const fetchData = async () => {
        setLoading(true);
        try {
            const data = await getAllSubjects();
            setSubjects(data.map(item => ({ ...item, key: item.id })));
        } catch (error) { message.error('Failed to load subjects.'); }
        finally { setLoading(false); }
    };

    const handleAdd = () => { setEditingSubject(null); form.resetFields(); setIsModalVisible(true); };
    const handleEdit = (record) => { setEditingSubject(record); form.setFieldsValue({ name: record.name }); setIsModalVisible(true); };
    const handleCancel = () => { setIsModalVisible(false); setEditingSubject(null); };

    const handleOk = () => {
        form.validateFields().then(async (values) => {
            try {
                if (editingSubject) {
                    await updateSubject(editingSubject.id, values.name);
                    message.success('Subject updated successfully!');
                } else {
                    await createSubject(values.name);
                    message.success('Subject created successfully!');
                }
                handleCancel();
                fetchData();
            } catch (error) { message.error(error.response?.data?.error || 'Failed to save subject.'); }
        });
    };

    const handleDelete = async (id) => {
        try {
            await deleteSubject(id);
            message.success('Subject deleted successfully!');
            fetchData();
        } catch (error) { message.error(error.response?.data?.error || 'Failed to delete subject.'); }
    };

    const columns = [
        { title: 'Subject Name', dataIndex: 'name', key: 'name' },
        {
            title: 'Action', key: 'action', width: 150, align: 'center',
            render: (_, record) => (
                <Space>
                    <Button icon={<EditOutlined />} onClick={() => handleEdit(record)} />
                    <Popconfirm okType='default' title="Delete this subject?" onConfirm={() => handleDelete(record.id)}>
                        <Button icon={<DeleteOutlined />} danger />
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <div>
            {/* --- NEW: Responsive Header --- */}
            <Space
                style={{ width: '100%', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}
            >
                <Title level={2} style={{ margin: 0 }}>Subject Management</Title>
                <Button
                    type="default"
                    icon={<PlusOutlined />}
                    onClick={handleAdd}
                    block={isMobile} // Use state for responsive block property
                >
                    Add New Subject
                </Button>
            </Space>

            <Table
                dataSource={subjects}
                columns={columns}
                loading={loading}
                bordered
                scroll={{ x: 'max-content' }} // Use max-content for better auto-width
                pagination={{
                    pageSize: pageSize,
                    showSizeChanger: true,
                    pageSizeOptions: [10, 20, 50, 100],
                    onShowSizeChange: (current, size) => setPageSize(size),
                    onChange: (page, size) => setPageSize(size),
                    responsive: true,
                    showTotal: (total) => `Total ${total} subjects`
                }}
            />
            <Modal okType='default' title={editingSubject ? "Edit Subject" : "Add New Subject"} open={isModalVisible} onOk={handleOk} onCancel={handleCancel}>
                <Form form={form} layout="vertical">
                    <Form.Item name="name" label="Subject Name" rules={[{ required: true }]}>
                        <Input />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default SubjectManagementPage;